import { ApolloError, useApolloClient } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useNavigate } from 'router'
import { useUser } from 'store'
import LoginEventCategory, {
  LoginEventSignInAction,
} from 'google-analytics/events/login'
import { TrackGoogleAnalyticsEvent } from 'google-analytics/google-analytics-tracker-functionality'
import { SUPPORT_PHONE_ALERT_KEY } from 'variables'

import OPERATION_LOGIN, {
  OPERATION_RESET_FAKE,
  OPERATION_LOGIN_SSO,
  OPERATION_LOGIN_SSO_CENTER,
  OPERATION_FAKE,
  OPERATION_TWO_FA_ACTIVATE,
  OPERATION_TWO_FA_SEND_CODE,
  OPERATION_TWO_FA_SESSION,
  OPERATION_GET_SPONSOR_LINK,
  OPERATION_GENERATE_SPONSOR_CODE,
  OPERATION_ENTER_DEMO_MODE,
  OPERATION_EXIT_DEMO_MODE
} from './operations-gql/mutations'

interface SponsorLink {
  first_name: string;
  last_name: string;
  email: string;
  default_selected_days?: number;
}

interface SponsorLinkResponse {
  success: boolean;
}

type Actions = {
  login: (
    email: string,
    password: string,
  ) => Promise<{ errors?: ApolloError | unknown, data?: ILogin }>
  twoFaActivate: (code: string) => Promise<{ errors?: ApolloError | unknown }>
  twoFaSendCode: () => Promise<{ errors?: ApolloError | unknown }>
  twoFaSession: (code:string) => Promise<{ errors?: ApolloError | unknown }>
  logout: () => Promise<void>
  fakeout: () => Promise<void>
  sso: () => Promise<void>
  ssoCenter: (token: string, redirect?: string) => Promise<void>
  fake: (email: string) => Promise<{ errors?: ApolloError | unknown }>
  enterDemoMode: (name: string) => Promise<{ errors?: ApolloError | unknown }>
  exitDemoMode: () => Promise<void>
  getSponsorLink: (token: string) => Promise<SponsorLink>
  generateSponsorCode: (token: string, ends_at: Date | string) => Promise<SponsorLinkResponse | null>
}

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return { };
  }
};

interface ILogin {
  two_fa_activated: boolean
  two_fa_disclaimer: boolean
  two_fa_token?: string
  two_fa_user_id?: string
}

const addTokensToStorage = (access_token, refresh_token) => {
  localStorage.setItem('ACCESS_TOKEN', access_token)
  localStorage.setItem('REFRESH_TOKEN', refresh_token)
}

const flagSupportPhoneAlert = () => {
  sessionStorage.setItem(SUPPORT_PHONE_ALERT_KEY, 'true')
}

export const cleanTwoFaStorage = () => {
  localStorage.removeItem('TWO_FA_TOKEN')
  localStorage.removeItem('TWO_FA_ACTIVATED')
  localStorage.removeItem('TWO_FA_USER_ID')
  localStorage.removeItem('TWO_FA_USER_EMAIL')
}
const useActions = (): Actions => {
  const navigate = useNavigate()
  const [, setUser] = useUser()
  const client = useApolloClient()
  const { i18n } = useTranslation()
  const twoFaSession = async (code: string): Promise<void> => {
    const { data } = await client.mutate({
      mutation: OPERATION_TWO_FA_SESSION,
      variables: {
        code,
        token: localStorage.getItem('TWO_FA_TOKEN')
      }
    })
    const { access_token, refresh_token } = data.tokens
    const {user_id} = parseJwt(access_token)
    await client.clearStore()
    addTokensToStorage(access_token, refresh_token)
    TrackGoogleAnalyticsEvent(
      LoginEventCategory,
      LoginEventSignInAction,
      window.location.pathname,
      { center_user_id: user_id, method: 'two_fa' },
    )
    flagSupportPhoneAlert()
    setTimeout(() => cleanTwoFaStorage(), 1000)
  }

  const login = async (email: string, password: string): Promise<ILogin> => {
    const { data } = await client.mutate({
      mutation: OPERATION_LOGIN,
      variables: {
        email,
        password,
        language: i18n.language
      },
    })
    const { access_token, refresh_token, two_fa_activated, two_fa_token, two_fa_disclaimer, two_fa_user_id } = data.login
    if (two_fa_activated) {
      localStorage.setItem('TWO_FA_ACTIVATED', '1')
      localStorage.setItem('TWO_FA_TOKEN', two_fa_token)
      localStorage.setItem('TWO_FA_USER_ID', two_fa_user_id)
      localStorage.setItem('TWO_FA_USER_EMAIL', email)
      return {
        two_fa_activated: two_fa_activated as boolean,
        two_fa_disclaimer: false
      }
    }
    const { user_id } = parseJwt(access_token)
    await client.clearStore()
    cleanTwoFaStorage()
    localStorage.setItem('TWO_FA_USER_ID', user_id)
    localStorage.setItem('TWO_FA_USER_EMAIL', email)
    localStorage.setItem('TWO_FA_ACTIVATED', '0')
    addTokensToStorage(access_token, refresh_token)
    TrackGoogleAnalyticsEvent(
      LoginEventCategory,
      LoginEventSignInAction,
      window.location.pathname,
      { center_user_id: user_id, method: 'password' },
    )
    flagSupportPhoneAlert()
    return {
      two_fa_activated: false,
      two_fa_disclaimer: two_fa_disclaimer,
    }
  }

  const sso = async (): Promise<void> => {
    const jwt = sessionStorage.getItem('jwt')
    if (!jwt) return
    
    const { data } = await client.mutate({
      mutation: OPERATION_LOGIN_SSO,
      variables: {
        token: jwt,
      },
    })
    const { access_token, refresh_token } = data.sso
    const {user_id} = parseJwt(access_token)
    addTokensToStorage(access_token, refresh_token)
    await client.clearStore()
    TrackGoogleAnalyticsEvent(
      LoginEventCategory,
      LoginEventSignInAction,
      window.location.pathname,
      { center_user_id: user_id, method: 'sso' },
    )
    flagSupportPhoneAlert()

  }

  const ssoCenter = async (token: string, redirect = '/'): Promise<void> => {
    const { data } = await client.mutate({
      mutation: OPERATION_LOGIN_SSO_CENTER,
      variables: {
        token,
      },
    })
    const { access_token, refresh_token } = data.sso_center
    const {user_id} = parseJwt(access_token)
    addTokensToStorage(access_token, refresh_token)
    TrackGoogleAnalyticsEvent(
      LoginEventCategory,
      LoginEventSignInAction,
      window.location.pathname,
      { center_user_id: user_id, method: 'sso_center' },
    )
    flagSupportPhoneAlert()
    
    const [pathname, search] = redirect.split('?')
    navigate({ 
      pathname, 
      search: search ? `?${search}` : undefined 
    })
  }
  
  const logout = async (): Promise<void> => {
    await client.clearStore()
    localStorage.removeItem('ACCESS_TOKEN')
    localStorage.removeItem('REFRESH_TOKEN')
    setUser(undefined)
    localStorage.removeItem('sites')
    await client.clearStore()
    navigate({ pathname: '/auth/login' })
  }

  const fakeout = async () => {
    const { data, errors } = await client.mutate({
      mutation: OPERATION_RESET_FAKE,
    })
    if (!errors) {
      const { access_token, refresh_token } = data.me
      addTokensToStorage(access_token, refresh_token)
      localStorage.removeItem('sites')
      await client.clearStore()
    }
  }

  const fake = async (email: string) => {
    const { data, errors } = await client.mutate({
      mutation: OPERATION_FAKE,
      variables: { email: email },
    })
    if (!errors) {
      const { access_token, refresh_token } = data.me
      addTokensToStorage(access_token, refresh_token)
      localStorage.removeItem('sites')
      await client.clearStore()
    } else {
      throw errors
    }
  }

  const enterDemoMode = async (name: string) => {
    const { data, errors } = await client.mutate({
      mutation: OPERATION_ENTER_DEMO_MODE,
      variables: { name },
    })
    if (!errors) {
      const { access_token, refresh_token } = data.me
      addTokensToStorage(access_token, refresh_token)
      await client.clearStore()
      localStorage.removeItem('sites')
    } else {
      throw errors
    }
  }

  const exitDemoMode = async () => {
    const { data, errors } = await client.mutate({
      mutation: OPERATION_EXIT_DEMO_MODE,
    })
    if (!errors) {
      const { access_token, refresh_token } = data.me
      addTokensToStorage(access_token, refresh_token)
      localStorage.removeItem('sites')
      await client.clearStore()
    }
  }

  const getSponsorLink = async (token: string): Promise<SponsorLink> => {
    const { data, errors } = await client.query({
      query: OPERATION_GET_SPONSOR_LINK,
      variables: { token },
      fetchPolicy: 'network-only',
    })
    
    if (errors) {
      throw errors
    }
    
    return data.sponsorLink
  }

  const generateSponsorCode = async (token: string, ends_at: Date | string): Promise<SponsorLinkResponse | null> => {
    const formattedDate = typeof ends_at === 'string' ? ends_at : ends_at.toISOString()
    
    const { data, errors } = await client.mutate({
      mutation: OPERATION_GENERATE_SPONSOR_CODE,
      variables: {
        token,
        ends_at: formattedDate
      },
    })
    
    if (errors) {
      throw errors
    }
    
    return data.generateSponsorCode || null
  }

  const twoFaActivate = async (code: string):Promise<boolean> => {
    const { errors, data } = await client.mutate({
      mutation: OPERATION_TWO_FA_ACTIVATE,
      variables: {
        code,
        token: localStorage.getItem('TWO_FA_TOKEN'),
        language: i18n.language
      }
    })
    if (errors) {
      throw errors
    }
    const { access_token, refresh_token } = data.tokens
    addTokensToStorage(access_token, refresh_token)
    flagSupportPhoneAlert()
    setTimeout(() => cleanTwoFaStorage(), 1000)
    return true
  }

  const twoFaSendCode = async () => {
    const isTwoFaActivated = localStorage.getItem('TWO_FA_ACTIVATED') === '1'
    const target = isTwoFaActivated
      ? { token: localStorage.getItem('TWO_FA_TOKEN') }
      : { user_id: localStorage.getItem('TWO_FA_USER_ID') }
    const { data, errors } = await client.mutate({
      mutation: OPERATION_TWO_FA_SEND_CODE,
      variables: {
        ...target,
        language: i18n.language
      }
    })
    if (errors) {
      throw errors
    }
    localStorage.setItem('TWO_FA_TOKEN', data.token)
  }
  return {
    sso,
    ssoCenter,
    login: async (
      email: string,
      password: string,
    ): Promise<{ errors?: ApolloError | unknown, data?: ILogin }> => {
      try {
        const data:ILogin = await login(email, password) as ILogin
        return { data }
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    twoFaSession: async (code: string): Promise<{ errors?: ApolloError | unknown }> => {
      try {
        await twoFaSession(code)
        return { errors: null }
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    twoFaActivate: async (code: string): Promise<{ errors?: ApolloError | unknown }> => {
      try {
        await twoFaActivate(code)
        cleanTwoFaStorage()
        return { errors: null }
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    twoFaSendCode: async () => {
      try {
        await twoFaSendCode()
        return { errors: null }
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    fake: async (email): Promise<{ errors?: ApolloError | unknown }> => {
      try {
        await fake(email)
        navigate({ pathname: '/' })
        return {}
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    enterDemoMode: async (name: string): Promise<{ errors?: ApolloError | unknown}> => {
      try {
        await enterDemoMode(name)
        return {}
      } catch (e: unknown) {
        return { errors: e }
      }
    },
    exitDemoMode,
    logout,
    fakeout,
    getSponsorLink,
    generateSponsorCode,
  }
}

export default useActions
